import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/useLanguage";
import { dayHeading } from "./dateUtils";

interface SeverityCount {
  high: number;
  medium: number;
  low: number;
  none: number;
}

interface PastResult {
  beats: number;
  misses: number;
}

interface DayRailProps {
  dates: Date[];
  today: Date;
  counts: Record<string, SeverityCount>;
  pastResults: Record<string, PastResult>;
  onSelect: (isoDate: string) => void;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

interface ChipProps {
  count: number;
  variant: "high" | "medium" | "low";
}

/**
 * Compact "dot + count" chip per severity. Replaces the prior dot-per-event
 * string which overflowed the day cell horizontally on busy days.
 */
function SeverityChip({ count, variant }: ChipProps) {
  if (count === 0) return null;
  const palette =
    variant === "high"
      ? "bg-red-500/15 text-red-400"
      : variant === "medium"
        ? "bg-amber-500/15 text-amber-400"
        : "bg-emerald-500/15 text-emerald-400";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10px] tabular-nums",
        palette,
      )}
    >
      <span aria-hidden="true">●</span>
      {count}
    </span>
  );
}

export function DayRail({
  dates,
  today,
  counts,
  pastResults,
  onSelect,
}: DayRailProps) {
  const { t } = useTranslation("calendar");
  const { language } = useLanguage();
  const todayLabel = t("nav.today");
  const todayIso = isoDate(today);
  return (
    <div className="flex w-44 shrink-0 flex-col gap-1">
      {dates.map((d) => {
        const iso = isoDate(d);
        const c = counts[iso] ?? { high: 0, medium: 0, low: 0, none: 0 };
        const past = pastResults[iso];
        const isToday = iso === todayIso;
        const totalUpcoming = c.high + c.medium + c.low;
        return (
          <button
            key={iso}
            type="button"
            onClick={() => onSelect(iso)}
            className={cn(
              "flex flex-col gap-1 rounded-md border px-2 py-1.5 text-start text-xs",
              isToday
                ? "border-l-4 border-l-emerald-500 bg-card font-semibold"
                : totalUpcoming === 0
                  ? "border-border bg-muted/30 text-muted-foreground"
                  : "border-border bg-card",
            )}
          >
            <span className="font-medium">
              {dayHeading(d, today, language, todayLabel)}
            </span>
            <div className="flex flex-wrap items-center gap-1">
              <SeverityChip count={c.high} variant="high" />
              <SeverityChip count={c.medium} variant="medium" />
              <SeverityChip count={c.low} variant="low" />
              {totalUpcoming === 0 && !past && (
                <span className="text-[10px] text-muted-foreground">—</span>
              )}
            </div>
            {past && (past.beats > 0 || past.misses > 0) && (
              <div className="flex gap-2 text-[10px]">
                {past.beats > 0 && (
                  <span className="text-emerald-400">✓ {past.beats}</span>
                )}
                {past.misses > 0 && (
                  <span className="text-red-400">✗ {past.misses}</span>
                )}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
